import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config';

const ResetPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.3)',
  maxWidth: 520,
  width: '100%',
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
}));

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const resolveRecoveryState = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;
        setHasRecoverySession(Boolean(session?.user));
      } finally {
        if (mounted) {
          setCheckingSession(false);
        }
      }
    };

    resolveRecoveryState();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setHasRecoverySession(Boolean(session?.user));
        setCheckingSession(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!hasRecoverySession) {
      setError('This password reset link is invalid or has expired. Request a new reset email.');
      return;
    }

    if (!password || !confirmPassword) {
      setError('Enter and confirm your new password.');
      return;
    }

    if (password.length < 8) {
      setError('Use at least 8 characters for your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match. Please try again.");
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }

      setSuccess('Password updated successfully. Redirecting to login...');
      window.setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1200);
    } catch (err: any) {
      setError(err?.message || 'Unable to update password right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 30%, #15152a 0%, #05070a 60%)',
        padding: { xs: 2, sm: 4 },
      }}
    >
      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <ResetPaper elevation={0}>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}>
            Set New Password
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 3 }}>
            Finish password recovery by choosing a new password for your developer account.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {checkingSession ? (
            <Alert severity="info">Validating your password reset session...</Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={loading || !hasRecoverySession}
                autoComplete="new-password"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
              <TextField
                label="Confirm New Password"
                type="password"
                fullWidth
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={loading || !hasRecoverySession}
                autoComplete="new-password"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />
              <Button
                type="submit"
                fullWidth
                disabled={loading || !hasRecoverySession}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #ff8b42, #8f63ff)',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.3,
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff7a2f, #7a4dff)',
                  },
                }}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 2.5 }}>
            Back to sign in?{' '}
            <Button component={Link} to="/login" sx={{ textTransform: 'none', color: '#ffae78', p: 0, minWidth: 0 }}>
              Return to login
            </Button>
          </Typography>
        </ResetPaper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
